import { useState, useEffect } from "react";
import { rootDoc } from "./firebase";
import { getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import "./GuestForm.css";

const WEDDING_DATE = new Date("2026-09-03T12:00:00");
const COUPLE_NAMES = "Keith Panashe Uchena & Margaret (Maggy) Bwaka";

export default function SecretGuestForm() {
  const [formData, setFormData] = useState({
    title: "Mr",
    name: "",
    surname: "",
    email: "",
    phone: "",
    behalfOf: "Keith",
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOnline) {
      setErrorMessage("You appear to be offline. Please check your internet connection and try again.");
      setShowErrorModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const email = formData.email.trim().toLowerCase();
      const localPart = email.split('@')[0];
      const domain = email.split('@')[1];

      const rolePrefixes = ['admin', 'info', 'support', 'noreply', 'contact', 'mailer-daemon', 'postmaster'];
      if (rolePrefixes.includes(localPart)) {
        setErrorMessage('Please use a personal email address, not a role-based one.');
        setShowErrorModal(true);
        return;
      }

      const disposableDomains = ['mailinator.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com', 'throwaway.email', 'yopmail.com', 'sharklasers.com', 'trashmail.com'];
      if (disposableDomains.includes(domain)) {
        setErrorMessage('Disposable email addresses are not allowed.');
        setShowErrorModal(true);
        return;
      }

      const commonDomains = { 'gamil.com': 'gmail.com', 'gmial.com': 'gmail.com', 'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'hotmai.com': 'hotmail.com', 'hotmal.com': 'hotmail.com', 'outlok.com': 'outlook.com' };
      if (commonDomains[domain]) {
        setErrorMessage(`Did you mean ${commonDomains[domain]}?`);
        setShowErrorModal(true);
        return;
      }

      const dnsRes = await fetch(`https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`);
      const dnsData = await dnsRes.json();
      const hasMxRecords = dnsData.Answer && dnsData.Answer.some(r => r.type === 15);
      if (!hasMxRecords) {
        setErrorMessage('Invalid or disposable email address. Please use a real email.');
        setShowErrorModal(true);
        return;
      }

      formData.email = email;
      const lateListRef = rootDoc("guests", "late_list");
      const lateListDoc = await getDoc(lateListRef);

      let currentGuests = [];
      if (lateListDoc.exists()) {
        currentGuests = lateListDoc.data().guests || [];
      }

      const maggyGuests = currentGuests.filter(guest => guest.behalfOf === "Maggy").length;
      const keithGuests = currentGuests.filter(guest => guest.behalfOf === "Keith").length;
      const totalGuests = currentGuests.length;

      if (formData.behalfOf === "Maggy") {
        if (maggyGuests >= 8) {
          setErrorMessage("Sorry, we've reached our guest capacity for Maggy's list. Please contact us if you have any questions.");
          setShowErrorModal(true);
          return;
        }
      } else if (formData.behalfOf === "Keith") {
        if (keithGuests >= 22) {
          setErrorMessage("Sorry, we've reached our guest capacity for Keith's list. Please contact us if you have any questions.");
          setShowErrorModal(true);
          return;
        }
      }

      if (totalGuests >= 30) {
        setErrorMessage("Sorry, we've reached our maximum capacity for this special list. Please contact us if you have any questions.");
        setShowErrorModal(true);
        return;
      }

      if (!lateListDoc.exists()) {
        await setDoc(lateListRef, {
          guests: [formData]
        });
      } else {
        await updateDoc(lateListRef, {
          guests: arrayUnion(formData)
        });
      }

      setShowSuccessModal(true);
      setFormData({ title:"Mr", name: "", surname: "", email: "", phone: "", behalfOf: "Keith" });
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(
        error.code === 'unavailable' || error.code === 'failed-precondition'
          ? "Unable to connect to our servers. Please check your internet connection and try again."
          : "There was an error submitting your RSVP. Please try again later."
      );
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const today = new Date();
  const timeDiff = WEDDING_DATE - today;
  const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  const isWeddingDay = daysLeft === 0 && today.toDateString() === WEDDING_DATE.toDateString();
  const isAfterWedding = today > WEDDING_DATE;
  const daysMarried = isAfterWedding ? Math.floor(Math.abs(timeDiff) / (1000 * 60 * 60 * 24)) : 0;
  
  return (
    <div className="guest-form-container secret-form">
      <div className="form-card">
        <h1 className="welcome-title">Special Invitation!</h1>
        <h2 className="couple-names">{COUPLE_NAMES}</h2>
        <div className="wedding-date">September 3, 2026</div>
        <div className="wedding-date">Cape Town, South Africa</div>

        {isWeddingDay ? (
          <div className="countdown-banner wedding-day-banner">
            <span className="days-counter">🎉 It's Our Wedding Day! 🎉</span>
          </div>
        ) : isAfterWedding ? (
          <div className="countdown-banner married-banner">
            <span className="days-counter">Married since {daysMarried} day{daysMarried !== 1 ? 's' : ''}!</span>
          </div>
        ) : (
          <div className="countdown-banner">
            <span className="days-counter">{daysLeft}</span> days to go!
          </div>
        )}
        
        <div className="welcome-message">
          <p>🎉 You've been given access to our special late registration list!</p>
          <p>This list has limited capacity. Please provide your details below to secure your spot at our celebration.</p>
          <p><strong>Limited spots remaining!</strong></p>
        </div>

        <h3 className="form-subtitle">Special Guest Registration</h3>
        
        <form onSubmit={handleSubmit}>
        <select 
            name="title" 
            value={formData.title} 
            onChange={handleChange} 
            className="input-field"
          >
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
          </select>
          <input 
            name="name" 
            placeholder="Name" 
            value={formData.name} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <input 
            name="surname" 
            placeholder="Surname" 
            value={formData.surname} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <input 
            type="email" 
            name="email" 
            placeholder="Email" 
            value={formData.email} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <input 
            type="tel" 
            name="phone" 
            placeholder="Phone Number" 
            value={formData.phone} 
            onChange={handleChange} 
            className="input-field" 
            required 
          />
          <select 
            name="behalfOf" 
            value={formData.behalfOf} 
            onChange={handleChange} 
            className="input-field"
          >
            <option value="Keith">Keith</option>
            <option value="Maggy">Maggy</option>
          </select>
          <button 
            type="submit" 
            className="submit-button" 
            disabled={isSubmitting || isAfterWedding}
          >
            {isSubmitting ? "Submitting..." : isAfterWedding ? "Wedding Complete" : "Secure My Spot"}
          </button>
        </form>
        
        <div className="thank-you-message">
          We're excited to celebrate with you!
        </div>
        
        {!isOnline && (
          <div className="offline-notice">
            You appear to be offline. Please connect to the internet to submit your RSVP.
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={closeSuccessModal}>
          <div className="success-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-icon">✓</div>
              <h3 className="modal-title">Spot Secured!</h3>
              <p className="modal-message">Your special RSVP has been received.</p>
              <p className="modal-message">You're now on our exclusive late registration list!</p>
              <p className="modal-message">We can't wait to celebrate our special day with you!</p>
              <button onClick={closeSuccessModal} className="modal-button">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={closeErrorModal}>
          <div className="error-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-icon error">!</div>
              <h3 className="modal-title">Oops!</h3>
              <p className="modal-message">{errorMessage}</p>
              <button onClick={closeErrorModal} className="modal-button">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
